"use client";
import { GoogleOAuthProvider, useGoogleOneTapLogin } from "@react-oauth/google";
import axios from "axios";
import { useToast } from "component/notifications";
import useUser, { useGoogleAuth } from "hooks/useUser";
import { AppProgressBar } from "next-nprogress-bar";
import { useEffect, useRef, useState } from "react";
import { mutate } from "swr";
import { init } from "utils/history";
import { refreshAxios } from "utils/fetcher";
import { useRouter } from "next/navigation";
import sanitizeData from "helpers/janitor";
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

axios.defaults.baseURL = process.env.NEXT_PUBLIC_BACKEND_URL;
axios.defaults.headers.common.Accept = "application/json";
axios.defaults.headers.post["Content-Type"] = "application/json";
if (typeof window !== "undefined" && window.localStorage.getItem("imprt")) {
  axios.defaults.headers.common["Authorization"] = window.localStorage.getItem(
    "uat"
  )
    ? "Bearer " + window.localStorage.getItem("uat")
    : undefined;
}

// Bare instance used only for token refresh — has no interceptors so it never
// triggers the response interceptor recursively.
// (defined in utils/fetcher.ts and shared here)
// console.log(axios.defaults.headers.common["Authorization"]);
// console.log("local", localStorage.getItem("imprt"));
// console.log("uat", localStorage.getItem("uat"));

axios.interceptors.request.use(
  (config) => {
    // Check if there's data to sanitize
    if (config.data) {
      config.data = sanitizeData(config.data); // Sanitize data recursively
    }
    return config;
  },
  (error) => {
    // Handle request error
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const code = error.response?.status || 0;
    const message =
      (code > 499 && code < 600
        ? "This service is currently unavailable, we are on it. Kindly check back later"
        : "") ||
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.response?.data?.non_field_errors ||
      error?.response?.data ||
      error?.message ||
      "Network Error";

    const isAuthError =
      code === 401 ||
      message === "Token is blacklisted" ||
      message === "Token is invalid or expired" ||
      message === "Given token not valid for any token type" ||
      message === "Authentication credentials were not provided.";

    if (isAuthError && !originalRequest?._retry) {
      originalRequest._retry = true;

      const refreshToken = typeof window !== "undefined" ? localStorage.getItem("imprt") : null;

      if (refreshToken) {
        try {
          // Use the interceptor-free instance to avoid triggering this
          // interceptor again for the refresh call itself.
          const { data } = await refreshAxios.post<{ access: string; refresh: string }>(
            "auth/jwt/refresh/",
            { refresh: refreshToken }
          );

          // Persist new tokens
          localStorage.setItem("imprt", data.refresh);
          localStorage.setItem("uat", data.access);
          axios.defaults.headers.common["Authorization"] = "Bearer " + data.access;
          originalRequest.headers["Authorization"] = "Bearer " + data.access;
        } catch {
          // Refresh failed — clear session and redirect to login
          localStorage.removeItem("imprt");
          localStorage.removeItem("uat");
          axios.defaults.headers.common["Authorization"] = undefined;
          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          return Promise.reject(message);
        }

        // Retry the original request OUTSIDE the try-catch so a retry failure
        // does not incorrectly clear the freshly-obtained tokens.
        return axios(originalRequest);
      } else {
        // No refresh token at all — redirect to login
        axios.defaults.headers.common["Authorization"] = undefined;
        if (typeof window !== "undefined" && window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }

    return Promise.reject(message);
  }
);

export default function Client() {
  // const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const toast = useToast();
  const { mutate } = useUser();
  const router = useRouter();

  const handleLogout = () => {
    if (localStorage.getItem("flwP") && localStorage.getItem("flwP") === "1") {
      // alert("Reset instead");
      resetTimer();
      return;
    }
    // Clear user session (e.g., remove JWT, clear cookies, etc.)
    if (localStorage.getItem("imprt") || localStorage.getItem("uat")) {
      toast({
        text: "Signed out due to 5 minutes inactivity",
        type: "warning",
      });
      axios.defaults.headers.common.Authorization = undefined;
      localStorage.removeItem("imprt");
      localStorage.removeItem("uat");
      mutate(undefined);
      // location.reload();
      router.push("/login"); // Redirect to the login page
    }
  };

  const resetTimer = () => {
    // console.log("RES: ", timerRef.current);
    // console.log("RES");

    if (timerRef.current) {
      clearTimeout(timerRef.current); // Clear the previous timer
      // console.log("CLR: ", timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      handleLogout(); // Log the user out after timeout
    }, INACTIVITY_TIMEOUT);
  };

  // const stopTimer = () => {
  //   if (timerRef.current) {
  //     clearTimeout(timerRef.current); // Clear the previous timer
  //     console.log("TIMER STOPPED");

  //     console.log("CLR: ", timerRef.current);
  //   }
  // };
  const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
  // Setup event listeners for user activity
  // useEffect(() => {
  //   // Start the timer on initial render
  //   if (typeof window !== "undefined") {
  //     resetTimer();

  //     // Reset the timer on user activity
  //     events.forEach((event) =>
  //       window.addEventListener(event, resetTimer, { passive: true })
  //     );
  //   }

  //   return () => {
  //     // Cleanup event listeners and timer on unmount
  //     if (typeof window !== "undefined") {
  //       if (timerRef.current) {
  //         clearTimeout(timerRef.current);
  //       }
  //       events.forEach((event) =>
  //         window.removeEventListener(event, resetTimer)
  //       );
  //     }
  //   };
  // }, []);
  useEffect(init, []);

  // const { data, isLoading } = useUser();
  // const { trigger } = useGoogleAuth();
  // const toast = useToast();
  // useGoogleOneTapLogin({
  //   disabled: Boolean(data) || isLoading,
  //   onSuccess(credentialResponse) {
  //     trigger({
  //       auth_token: credentialResponse.credential!,
  //     }).catch((e) => {
  //       toast({
  //         type: "warning",
  //         text: `Error: ${JSON.stringify(e)}`,
  //       });
  //     });
  //   },
  // });
  // useEffect(() => {
  //   // if (!getApps().length) {
  //   initializeApp(firebaseConfig);
  //   // }
  // }, []);
  return (
    <>
      <AppProgressBar
        height="4px"
        color="#43a45e"
        options={{ showSpinner: false }}
        shallowRouting
      />

    </>
  );
}

export function GoogleProvider(props: React.PropsWithChildren) {
  return props.children;
  // <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!}>

  // </GoogleOAuthProvider>
}
